import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  preventDefault,
  useContainer,
  useEditor,
  useEditorComponents,
  useMenuIsOpen,
  useTranslation,
  TldrawUiMenuContextProvider,
} from 'tldraw';
import { ContextMenu as RadixContextMenu } from 'radix-ui';
import { peekClipboardMenuLabel } from '../utils/canvasEmptySpaceActions';
import {
  isSyntheticBoardContextMenu,
  syncContextMenuPagePoint,
} from '../utils/openTldrawContextMenu';
import BoardContextMenuContent, { isEmptyCanvasContextMenu } from './BoardContextMenuContent';

/**
 * Controlled clone of tldraw's DefaultContextMenu.
 *
 * DefaultContextMenu keeps Radix uncontrolled (`open` not passed) while gating
 * content with tldraw's menu atom. MenuClickCapture calls clearOpenMenus() on
 * dismiss, which unmounts content without telling Radix — Root stays "open" and
 * the next right-click never opens the menu. Passing open={isOpen} keeps them synced.
 */
const BoardContextMenu = memo(function BoardContextMenu({
  children,
  disabled = false,
}) {
  const editor = useEditor();
  const msg = useTranslation();
  const { Canvas } = useEditorComponents();

  const preventEscapeFromLosingShapeFocus = useCallback(
    e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        editor.getContainer().focus();
      }
    },
    [editor]
  );

  useEffect(() => {
    return () => {
      document.body.removeEventListener('keydown', preventEscapeFromLosingShapeFocus, {
        capture: true,
      });
    };
  }, [preventEscapeFromLosingShapeFocus]);

  const onOpenChangeExtra = useCallback(
    isOpen => {
      if (!isOpen) {
        const onlySelectedShape = editor.getOnlySelectedShape();
        if (onlySelectedShape && editor.isShapeOrAncestorLocked(onlySelectedShape)) {
          editor.setSelectedShapes([]);
        }
        editor.timers.requestAnimationFrame(() => {
          document.body.removeEventListener('keydown', preventEscapeFromLosingShapeFocus, {
            capture: true,
          });
        });
      } else {
        document.body.addEventListener('keydown', preventEscapeFromLosingShapeFocus, {
          capture: true,
        });

        if (editor.getInstanceState().isCoarsePointer) {
          const selectedShapes = editor.getSelectedShapes();
          const {
            inputs: { currentPagePoint },
          } = editor;
          const shapesAtPoint = editor.getShapesAtPoint(currentPagePoint);

          if (
            !editor.getSelectedShapes().length ||
            !shapesAtPoint.some(s => selectedShapes.includes(s))
          ) {
            const lockedShapes = shapesAtPoint.filter(s => editor.isShapeOrAncestorLocked(s));
            if (lockedShapes.length) {
              editor.select(...lockedShapes.map(s => s.id));
            }
          }
        }
      }
    },
    [editor, preventEscapeFromLosingShapeFocus]
  );

  const container = useContainer();
  const [isOpen, handleOpenChange] = useMenuIsOpen('context menu', onOpenChangeExtra);
  const [clipboardLabel, setClipboardLabel] = useState(null);
  // Empty-canvas menu can grow a Paste row. Hold the menu until that check
  // finishes so the row is not inserted after the menu is already visible.
  const [clipboardPending, setClipboardPending] = useState(false);

  // Start the peek on the right-click itself so clipboard.read() still has
  // a user gesture when the menu opens a frame later.
  useEffect(() => {
    const canvas = editor.getContainer();
    if (!canvas) return undefined;
    let seq = 0;
    let timeout = 0;
    const onContextMenu = event => {
      if (isSyntheticBoardContextMenu(event)) return;
      window.clearTimeout(timeout);
      if (editor.getInstanceState()?.isReadonly) {
        seq += 1;
        setClipboardPending(false);
        setClipboardLabel(null);
        return;
      }
      syncContextMenuPagePoint(editor, event.clientX, event.clientY);
      if (!isEmptyCanvasContextMenu(editor)) {
        seq += 1;
        setClipboardPending(false);
        setClipboardLabel(null);
        return;
      }
      const id = ++seq;
      let settled = false;
      setClipboardLabel(null);
      setClipboardPending(true);
      const finish = label => {
        if (settled || id !== seq) return;
        settled = true;
        window.clearTimeout(timeout);
        setClipboardLabel(label);
        setClipboardPending(false);
      };
      timeout = window.setTimeout(() => finish(null), 800);
      peekClipboardMenuLabel()
        .then(label => finish(label))
        .catch(() => finish(null));
    };
    canvas.addEventListener('contextmenu', onContextMenu, true);
    return () => {
      window.clearTimeout(timeout);
      canvas.removeEventListener('contextmenu', onContextMenu, true);
    };
  }, [editor]);

  const showMenu = isOpen && !clipboardPending;
  const content = children ?? <BoardContextMenuContent clipboardLabel={clipboardLabel} />;

  return (
    <RadixContextMenu.Root
      dir="ltr"
      open={isOpen}
      onOpenChange={handleOpenChange}
      modal={false}
    >
      <RadixContextMenu.Trigger onContextMenu={undefined} dir="ltr" disabled={disabled}>
        {Canvas ? <Canvas /> : null}
      </RadixContextMenu.Trigger>
      {showMenu && (
        <RadixContextMenu.Portal container={container}>
          <RadixContextMenu.Content
            className="tlui-menu tlui-scrollable"
            data-testid="context-menu"
            aria-label={msg('context-menu.title')}
            alignOffset={-4}
            collisionPadding={4}
            onContextMenu={preventDefault}
            onCloseAutoFocus={e => {
              // Keep focus free for post-action UI (e.g. frame title editing).
              e.preventDefault();
            }}
          >
            <TldrawUiMenuContextProvider type="context-menu" sourceId="context-menu">
              {content}
            </TldrawUiMenuContextProvider>
          </RadixContextMenu.Content>
        </RadixContextMenu.Portal>
      )}
    </RadixContextMenu.Root>
  );
});

export default BoardContextMenu;
